import { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';

export type VideoPlayerMode = 'click' | 'hover' | 'in-view' | 'background';

export interface UseVideoPlaybackParams {
  mode: VideoPlayerMode;
  isActive?: boolean;
  isInView: boolean;
}

export interface HoverHandlers {
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
}

export interface VideoPlayback extends HoverHandlers {
  playing: boolean;
  muted: boolean;
  /**
   * The heavy player is only mounted once playback is wanted for the first time, and then stays
   * mounted: leaving the viewport pauses it instead of tearing it down, so coming back neither
   * re-fetches the stream nor re-runs react-player's lazy loader.
   */
  shouldMount: boolean;
  setPlaying: (playing: boolean) => void;
  togglePlay: () => void;
  toggleMute: () => void;
}

const NO_HOVER_HANDLERS: HoverHandlers = {
  handleMouseEnter: () => {},
  handleMouseLeave: () => {},
};

function createHoverHandlers(
  isHoverMode: boolean,
  onIntent: () => void,
  setPlaying: (playing: boolean) => void,
): HoverHandlers {
  if (!isHoverMode) {
    return NO_HOVER_HANDLERS;
  }

  return {
    // Pointer events are low-priority updates for React; the mount they trigger is committed
    // synchronously so react-player's lazy loader cannot run before the mount lifecycle settles.
    handleMouseEnter: () =>
      flushSync(() => {
        onIntent();
        setPlaying(true);
      }),
    handleMouseLeave: () => setPlaying(false),
  };
}

/**
 * Owns the play/mute state of a `VideoPlayer` and translates the tile's `mode` into a
 * "mount on intent" decision, so nothing is fetched before the video is actually wanted.
 */
export function useVideoPlayback({
  mode,
  isActive,
  isInView,
}: UseVideoPlaybackParams): VideoPlayback {
  const isAutomatic = mode === 'in-view' || mode === 'background';
  const [hasIntent, setHasIntent] = useState(false);
  const [hasUserPaused, setHasUserPaused] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  // An ambient background video can be switched off too - the hero hands its stop button through
  // as `isActive`, and a tile that never passes the prop keeps playing whenever it is in view.
  const shouldPlayAutomatically = isInView && isActive !== false;

  useEffect(() => {
    if (!isAutomatic) {
      return;
    }

    setPlaying(shouldPlayAutomatically && !hasUserPaused);
    if (shouldPlayAutomatically) {
      setHasIntent(true);
    }
  }, [isAutomatic, shouldPlayAutomatically, hasUserPaused]);

  // Losing the active slot stops the video in every mode, not only in the automatic ones: a
  // video the user started with a click would otherwise keep running after a swipe to the next
  // slide, out of sight and possibly with sound. A manual pause is forgotten along with it, so
  // the slide plays again by itself the next time it becomes the active one, continuing from
  // wherever it stopped.
  useEffect(() => {
    if (isActive === false) {
      setPlaying(false);
      setHasUserPaused(false);
    }
  }, [isActive]);

  const togglePlay = () => {
    // Remember a manual pause so the in-view effect does not immediately resume playback.
    if (isAutomatic) {
      setHasUserPaused(playing);
    } else {
      setHasIntent(true);
    }

    setPlaying(!playing);
  };

  const hoverHandlers = useMemo(
    () => createHoverHandlers(mode === 'hover', () => setHasIntent(true), setPlaying),
    [mode],
  );

  // Automatic modes mount in the very commit that brought the player into view; the effect above
  // then makes that sticky, so the player survives leaving the viewport.
  const shouldMount = hasIntent || (isAutomatic && shouldPlayAutomatically);

  return {
    playing,
    muted,
    shouldMount,
    setPlaying,
    togglePlay,
    toggleMute: () => setMuted((current) => !current),
    ...hoverHandlers,
  };
}
